<template>
  <!-- Table Container Card -->
  <b-card
    no-body
  >
    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <Button
            v-if="getUserPermission('ADD_COLLATERAL_INFORMATION')"
            label="Add"
            size="md"
            @click="showModal('modal-lg')"
          />
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              label="Filter"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              label="Reset Filter"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>

    </div>

    <b-table
      ref="standingOrderRef"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      :items="collateralManagementList"
      responsive
      :fields="tableHeaders"
      primary-key="id"
      show-empty
      empty-text="No records available"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(collateralType)="data">
        {{ addSpaceToString(data.value) }}
      </template>

      <template #cell(collateralValuation)="data">
        {{ data.value ? toBalance(data.value) : '' }}
      </template>

      <template #cell(dateCreated)="data">
        {{ data.value ? toDateWithYear(data.value) : '' }}
      </template>

      <template #cell(status)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value)"
        >
          {{ data.value }}
        </b-badge>
      </template>
      <template #cell(enforceValidation)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value === true ? 'Yes' : 'No' )"
        >
          {{ data.value === true ? 'Yes' : 'No' }}
        </b-badge>
      </template>

      <!-- Column: Actions -->
      <template
        #cell(actions)="data"
      >
        <div class="text-nowrap">
          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >

            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              v-if="getUserPermission('MODIFY_COLLATERAL_INFORMATION')"
              @click="onSelectCollateralItem(data.item, $event.target, 'edit')"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">Modify</span>
            </b-dropdown-item>

            <b-dropdown-item @click="onSelectCollateralItem(data.item, $event.target, 'view')">
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50">View</span>
            </b-dropdown-item>

            <b-dropdown-item
              v-if="getUserPermission(data.item.status === 'Active' ? 'DEACTIVATE_COLLATERAL_INFORMATION' : 'ACTIVATE_COLLATERAL_INFORMATION')"
              @click="collateralInfoStatus(data.item, $event.target)"
            >
              <feather-icon icon="CheckSquareIcon" />
              <span class="align-middle ml-50">{{ data.item.status === 'Active' ? 'Deactivate' : 'Activate' }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />
    <!-- modal -->
    <b-modal
      id="modal-lg"
      ref="my-modal"
      :ok-title="buttonLabel"
      cancel-title="Cancel"
      cancel-variant="outline-secondary"
      centered
      no-close-on-backdrop
      size="lg"
      :title="modalTitle"
      :ok-disabled="isLoading"
      @ok="handleSubmit"
      @hidden="resetModal"
    >
      <validation-observer ref="collateralRules">
        <b-form>
          <b-row class="match-height">
            <b-col md="6">
              <SelectField
                id="pfi-code"
                v-model="collateralFormData.partnerCode"
                :label="$t('Partner Code')"
                :reduce="option => option.value"
                :options="pfiPartnerOptions"
                :placeholder="$t('Select')"
                :error-name="$t('Partner Code')"
                rules="required"
                item="label"
                :readonly="isReadonly"
                :set-selected="getLinkedPfiByPartnerCode"
              />
            </b-col>
            <b-col md="6">
              <InputField
                id="pfi-name"
                v-model="collateralFormData.partnerName"
                :label="$t('Partner Name')"
                type="text"
                readonly
              />
            </b-col>
            <b-col md="6">
              <InputField
                id="pfi-email-address"
                v-model="collateralFormData.partnerEmailAddress"
                :label="$t('Partner Email Address')"
                type="text"
                readonly
              />
            </b-col>
            <b-col
              md="6"
            >
              <SelectField
                id="collateral-type"
                v-model="collateralFormData.collateralType"
                label="Type of Collateral"
                placeholder="Select"
                :options="collateralTypeOptions"
                :reduce="option => option.value"
                error-name="Type of Collateral"
                rules="required"
                item="label"
                :readonly="isReadonly"
              />
            </b-col>

            <b-col md="6">
              <InputNumberField
                id="collateral-valuation"
                v-model="collateralFormData.collateralValuation"
                label="Collateral Valuation"
                placeholder="Enter Collateral Valuation"
                type="text"
                error-name="Collateral Valuation"
                rules="required"
                for="name"
                :readonly="isReadonly"
              />
            </b-col>
            <b-col md="6">
              <InputField
                id="detailed-description"
                v-model="collateralFormData.collateralDescription"
                label="Detailed Description"
                placeholder="Enter description"
                type="text"
                error-name="Detailed Description"
                rules="required|max: 150"
                for="name"
                :readonly="isReadonly"
              />
            </b-col>
            <b-col md="6">
              <InputField
                id="detailed-current-location"
                v-model="collateralFormData.location"
                label="Collateral Current Location"
                placeholder="Enter current location"
                type="text"
                error-name="Collateral Current Location"
                rules="required|max: 150"
                for="name"
                :readonly="isReadonly"
              />
            </b-col>
            <b-col
              md="6"
            >
              <SelectField
                id="collateral-currency-domiciliation"
                v-model="collateralFormData.currencyCode"
                label="Collateral's Currency Domiciliation"
                placeholder="Select"
                :options="currencyDomiciliationTypeOptions"
                :reduce="option => option.value"
                error-name="Collateral's Currency Domiciliation"
                rules="required"
                item="label"
                :readonly="isReadonly"
              />
            </b-col>
            <b-col
              md="6"
            >
              <SelectField
                id="perfect-status"
                v-model="collateralFormData.perfectionStatus"
                label="Collateral Perfection Status"
                placeholder="Select"
                :options="perfectionStatusOptions"
                :reduce="option => option.value"
                error-name="Collateral Perfection Status"
                rules="required"
                item="label"
                :readonly="isReadonly"
              />
            </b-col>
            <b-col md="6">
              <SwitchButton
                v-model="collateralFormData.enforceValidation"
                label="Enforced Validation"
                name="enforced-validation"
                class="mt-2"
                :readonly="isReadonly"
              />
            </b-col>
          </b-row>
          <div class="w-full mt-4 p-10">
            <div class="d-flex mb-0 mt-2 align-items-center justify-content-between">
              <h4>Collateral Documents Upload</h4>

              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="primary"
                class="ml-4"
                @click="addNewDocument()"
              >
                <feather-icon
                  icon="PlusIcon"
                  class="mr-25"
                />
                <span>Add New Document</span>
              </b-button>
            </div>
            <b-row
              v-for="(item, index) in documentsFormData"
              :id="index"
              :key="index"
              class="mb-2"
            >
              <b-col
                md="6"
              >
                <label>Upload Documents</label>

                <div class="upload-image">
                  <Spinner :show="item.loading" />
                  <validation-provider
                    v-slot="{ errors }"
                    :name="`Document File Upload - (${index + 1})`"
                    :rules="fileRules"
                  >
                    <b-form-group
                      :state="!errors.length"
                      :invalid-feedback="errors[0] || item.validationError"
                    >
                      <b-form-file
                        v-model="item.url"
                        :state="!errors.length && !item.validationError"
                        placeholder="Choose a file or drop it here..."
                        drop-placeholder="Drop file here..."
                        accept="image/jpeg, image/bmp, image/png, image/jpg, application/pdf, .doc, .docx, application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        plain
                        @change="onDocumentsUpload(index, $event)"
                      />
                      <div
                        v-if="!item.url"
                        class="upload-instruction"
                      >
                        <h4>Upload Document File</h4>
                        <p>Drag & drop file or choose from your device to upload</p>
                        <p class="format">
                          Format accepted (jpg, png, bmp, jpeg, docx, pdf)
                        </p>
                      </div>
                      <div v-else>
                        <div v-if="item.fileExtension === 'png'|| item.fileExtension === 'jpeg'|| item.fileExtension === 'jpg'|| item.fileExtension === 'bmp'">
                          <b-img
                            :src="item.url"
                            block
                            rounded
                            width="250px"
                            height="250px"
                          />
                        </div>
                        <div
                          v-if="item.fileExtension === 'pdf'"
                        >
                          <b-img
                            :src="pdfIcon"
                            block
                            rounded
                            width="250px"
                            height="250px"
                          />
                        </div>
                        <div
                          v-else-if="item.fileExtension === 'vnd'"
                        >
                          <b-img
                            :src="wordIcon"
                            block
                            rounded
                            width="250px"
                            height="250px"
                          />
                        </div>
                        <div
                          v-else
                        >
                          <b-img
                            src=""
                          />
                        </div>
                        <div
                          v-if="item.url"
                          class="trash"
                        >
                          <feather-icon
                            icon="Trash2Icon"
                            size="20"
                            class="text-primary"
                            @click="removeDocument(item.id, index)"
                          />
                        </div>
                        <div
                          v-if="item.url"
                          class="view-details"
                        >
                          <feather-icon
                            icon="EyeIcon"
                            size="20"
                            class="text-primary"
                            @click="viewDocumentFile(item.url, item.fileName, item.fileExtension)"
                          />
                        </div>
                      </div>
                    </b-form-group>
                  </validation-provider>
                </div>
              </b-col>
              <b-col
                md="3"
                class="mt-0"
              >
                <b-row>
                  <!-- Remove Button -->
                  <b-col
                    v-if="item.url"
                    md="12"
                    class="mt-1"
                  >
                    <b-button
                      v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                      variant="primary"
                      class="ml-0"
                      @click="removeDocument(item.id, index)"
                    >
                      <feather-icon
                        icon="XIcon"
                        class="mr-25"
                      />
                      <span>Remove</span>
                    </b-button>
                  </b-col>
                  <b-col
                    v-if="item.url"
                    md="12"
                    class="mt-1"
                  >
                    <b-button
                      v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                      variant="primary"
                      class="ml-0"
                      @click="handleDownload(item.url)"
                    >
                      <feather-icon
                        icon="DownloadIcon"
                        class="mr-25"
                      />
                      <span>Download</span>
                    </b-button>
                  </b-col>
                </b-row>
              </b-col>
            </b-row>
          </div>
        </b-form>
      </validation-observer>
      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->

    <b-modal
      id="view-avatar-modal"
      ref="avatarModal"
      no-close-on-backdrop
      ok-title="Download File"
      cancel-title="Cancel"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      :title="viewDocumentModalTitle"
      @hidden="resetDocumentModal"
      @cancel="resetDocumentModal"
      @ok="handleDownload(viewDocumentData)"
    >
      <div class="m-2 text-center">
        <b-row>
          <b-col cols="12">
            <div
              v-if="
                fileExtension === 'png' ||
                  fileExtension === 'jpeg' ||
                  fileExtension === 'jpg'
              "
            >
              <b-img
                :src="viewDocumentData"
                block
                fluid
                rounded
              />
            </div>
            <div v-else-if="fileExtension === 'pdf'">
              <b-img
                :src="pdfIcon"
                block
                width="250px"
                height="250px"
                rounded
              />
            </div>
            <div v-else-if="fileExtension === 'vnd'">
              <b-img
                :src="wordIcon"
                block
                width="250px"
                height="250px"
                rounded
              />
            </div>
            <div v-else>
              <b-img src="" />
            </div>
          </b-col>
        </b-row>
      </div>
    </b-modal>

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      ok-title="Apply"
      cancel-title="Cancel"
      cancel-variant="outline-secondary"
      no-close-on-backdrop
      centered
      size="lg"
      title="Filters"
      modal-footer
      @ok="fetchCollateralManagement"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <b-col md="6">
          <SelectField
            id="collateral-type"
            v-model="filter.collateralType"
            label="Collateral Type"
            :reduce="option=>option.value"
            placeholder="Select"
            :options="collateralTypeOptions"
            item="label"
          />
        </b-col>
        <b-col md="6">
          <SelectField
            id="status"
            v-model="filter.perfectionStatus"
            label="Collateral Perfection Status"
            :reduce="option=>option.value"
            placeholder="Select"
            :options="perfectionStatusOptions"
            item="label"
          />
        </b-col>
        <b-col md="6">
          <SelectField
            id="status"
            v-model="filter.status"
            label="Status"
            :reduce="option=>option.value"
            placeholder="Select"
            :options="statusOptions"
            item="label"
          />
        </b-col>
        <!-- Status -->
      </b-row>
    </b-modal>
    <!-- Filter modal -->
    <!-- Display Validation Errors -->
    <b-modal
      id="all-validated-modal"
      ref="allValidatedModal"
      ok-title="Cancel"
      ok-only
      no-close-on-backdrop
      ok-variant="outline-secondary"
      centered
      size="md"
      title="Kindly attend to the field(s) below"
      @hidden="resetValidationModal"
    >
      <div
        v-for="(errorItem, index) in allValidationErrors"
        :key="index"
      >
        <p>{{ `${index + 1}.` }} <span style="color: red;"> {{ errorItem }}</span></p>
      </div>
    </b-modal>
    <!-- Display Validation Errors -->
    <FullScreenSpinner :show="showSpinner" />
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BSpinner, BModal, BBadge, BForm, BDropdownItem, BDropdown,
  BFormGroup, BFormFile, BImg, BButton,
} from 'bootstrap-vue'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import { required } from '@validations'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { mapActions, mapGetters } from 'vuex'
import { permissionMixin } from '@core/mixins/permission'
import { v4 as uuidv4 } from 'uuid'
import Ripple from 'vue-ripple-directive'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import Spinner from '@/views/components/spinner/Spinner.vue'
import InputField from '../../../forms/InputField.vue'
import InputNumberField from '../../../forms/InputNumberField.vue'
import SelectField from '../../../forms/SelectField.vue'
import getStatusColor from '../../../../helpers/getStatusColor'
import Button from '../../../components/button/Button.vue'
import { lmsPermissions } from '../../../../helpers/permissions'
import SwitchButton from '../../../components/switch/Switch.vue'
import addSpaceToString from '../../../../helpers/addSpaceToString'
import { toDateWithYear } from '../../../../helpers/formatDate'
import { toKobo, toBalance } from '../../../../helpers/formatAmount'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BTable,
    BModal,
    BForm,
    BBadge,
    BFormGroup,
    BFormFile,
    BImg,
    BButton,
    BSpinner,
    SelectField,
    TablePagination,
    TableEntries,
    ValidationObserver,
    ValidationProvider,
    InputField,
    InputNumberField,
    BDropdownItem,
    BDropdown,
    FullScreenSpinner,
    Spinner,
    Button,
    SwitchButton,
  },
  directives: {
    Ripple,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      fileRules: 'required',
      addSpaceToString,
      toDateWithYear,
      toBalance,
      isViewOnly: false,
      isReadonly: false,
      isEditable: false,
      buttonLabel: 'Save',
      sortBy: 'description',
      required,
      modalTitle: 'Create A Collateral Information',
      sortDesc: false,
      showSpinner: false,
      isCreditCasaBranch: false,
      isDebitCasaBranch: false,
      getStatusColor,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'collateralType', sortable: true, label: 'CollateralType' },
        { key: 'collateralValuation', sortable: true, label: 'Collateral Valuation' },
        { key: 'collateralDescription', sortable: true, label: 'Collateral Description' },
        { key: 'location', sortable: true, label: 'Location' },
        { key: 'currencyCode', sortable: true, label: 'Currency Code' },
        { key: 'referenceCode', sortable: true, label: 'Reference Code' },
        { key: 'enforceValidation', sortable: true, label: 'Enforce Validation' },
        { key: 'perfectionStatus', sortable: true, label: 'Perfection Status' },
        { key: 'status', sortable: true, label: 'Status' },
        { key: 'dateCreated', sortable: true, label: 'Date Created' },
        { key: 'actions' },
      ],
      collateralFormData: {
        partnerCode: '',
        partnerName: '',
        partnerEmailAddress: '',
        collateralType: '',
        collateralValuation: '',
        collateralDescription: '',
        location: '',
        currencyCode: '',
        perfectionStatus: 0,
        enforceValidation: false,
        id: '',
      },
      filter: {
        collateralType: '',
        perfectionStatus: '',
        status: '',
      },

      collateralManagementList: [],
      collateralTypeOptions: [
        { value: 'RealEstateLandAndBuilding ', label: 'Real Estate/Land & Building' },
        { value: 'Automobile', label: 'Automobile' },
        { value: 'PlantEquipment', label: 'Plant & Equipment' },
        { value: 'NaturalReserves', label: 'Natural Reserves' },
        { value: 'MarketableSecurities', label: 'Marketable Securities' },
        { value: 'Dormiciliation', label: 'Dormiciliation' },
        { value: 'TreasuryBills', label: 'Treasury Bills' },
        { value: 'Bonds', label: 'Bonds' },
      ],
      currencyDomiciliationTypeOptions: [
        { value: 'NGN', label: 'NGN' },
        { value: 'Dollars', label: 'Dollars' },
      ],
      perfectionStatusOptions: [
        { value: 'Yes', label: 'Yes' },
        { value: 'No', label: 'No' },
      ],
      statusOptions: [
        { value: 'Active', label: 'Active' },
        { value: 'Inactive', label: 'Inactive' },
      ],
      showFormErrorAlert: false,
      viewDocumentData: '',
      viewDocumentModalTitle: '',
      fileExtension: '',
      documentLoadSpinner: false,
      documentIdentifier: '',
      documentsFormData: [{
        type: '',
        url: '',
        file: '',
        loading: false,
        validationError: '',
      }],
      pfiPartnerOptions: [],
      allValidationErrors: [],
    }
  },
  computed: {
    ...mapGetters({
      branchOptions: 'operations/branchOptions',
    }),
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchCollateralManagement()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchCollateralManagement()
    },
  },
  mounted() {
    this.fetchCollateralManagement()
    this.fetchAllPartnerCodes()
  },

  methods: {
    ...mapActions({
      getCollateralInformation: 'account/getcollateralManagementList',
      createCollateral: 'account/createCollateralAction',
      updateCollateral: 'account/updateCollateralAction',
      updateCollateralStatus: 'account/updateStatus',
      uploadDocumentImages: 'account/uploadDocumentImagesAction',
      getCollateralDocumentById: 'account/getCollateralDocumentById',
      deleteDocumentsUpload: 'account/deleteDocumentsUploadAction',
      getAllPartnerCodes: 'account/getAllPartnerCodes',
      getPfiByPartnerCode: 'account/getPfiByPartnerCode',
    }),
    resetValidationModal() {
      this.allValidationErrors = []
    },
    addNewDocument() {
      this.documentsFormData.push({
        type: '',
        url: '',
        file: '',
        loading: false,
      })
    },
    viewDocumentFile(image, title, fileExtension) {
      this.fileExtension = fileExtension
      this.viewDocumentData = image
      this.viewDocumentModalTitle = title.replace(/([a-zA-Z])(?=[A-Z])/g, '$1 ')
      this.$root.$emit('bv::show::modal', 'view-avatar-modal')
    },
    resetDocumentModal() {
      this.viewDocumentData = ''
      this.viewDocumentModalTitle = ''
    },
    handleDownload(data) {
      const link = document.createElement('a')
      link.href = data
      link.click()
      this.alertSuccess({
        text: 'Your request to download file was successful',
      })
    },
    async removeDocument(id, index) {
      try {
        this.documentLoadSpinner = true
        const formData = new FormData()
        formData.append('id', id)
        const response = await this.deleteDocumentsUpload({
          formData,
          permission: lmsPermissions.VIEW_COLLATERAL_INFORMATION,
        })
        const { status } = response
        if (status === 200) {
          this.documentLoadSpinner = false
          this.documentsFormData.splice(index, 1)
          this.alertSuccess({ text: 'Document Removed' })
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          this.alertError({
            text: `Delete failed, Reason: ${responseError.errorMessage}`,
          })
        })
      } finally {
        this.documentLoadSpinner = false
      }
    },
    async onDocumentsUpload(index, event) {
      const item = this.documentsFormData[index]
      try {
        item.loading = true
        const { files } = event.target

        // Reset any previous error
        item.validationError = ''

        // Basic validations
        if (!files[0]) {
          item.validationError = 'Please select a file.'
          return
        }

        const validTypes = [
          'image/jpeg',
          'image/png',
          'image/jpg',
          'image/bmp',
          'application/pdf',
          'application/msword',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        ]

        if (!validTypes.includes(files[0].type)) {
          item.validationError = 'Unsupported file type.'
          item.loading = false
          return
        }

        const maxSizeMB = 5 // 5 MB
        if (files[0].size > maxSizeMB * 1024 * 1024) {
          item.validationError = `File size exceeds ${maxSizeMB}MB limit.`
          item.loading = false
          return
        }
        const formData = new FormData()
        formData.append('attachedFile', files[0])
        formData.append('type', 'Others')
        formData.append('referenceNumber', this.documentIdentifier)
        const response = await this.uploadDocumentImages({ formData, permission: lmsPermissions.VIEW_COLLATERAL_INFORMATION })
        const { status, data } = await response
        if (status === 200 && data) {
          item.url = data.url
          item.id = data.id
          item.fileExtension = data.fileExtension
          item.loading = false
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          this.alertError({ text: `Your request to upload image failed. Reason: ${responseError.errorMessage}` })
        })
      } finally {
        item.loading = false
      }
    },
    async fetchCollateralManagement() {
      this.documentIdentifier = uuidv4()
      try {
        this.isBusy = true
        this.hideModal('modal-filter')
        const response = await this.getCollateralInformation({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          permission: lmsPermissions.VIEW_COLLATERAL_INFORMATION,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.collateralManagementList = data.data
          this.documentIdentifier = data.data.documentIdentifier
          this.totalCount = data.recordCount
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async fetchAllPartnerCodes() {
      try {
        const response = await this.getAllPartnerCodes({
          permission: lmsPermissions.VIEW_COLLATERAL_INFORMATION,
        })
        const { data, status } = response
        if (status === 200 && data.data) {
          this.pfiPartnerOptions = data.data.map(item => ({ label: `${item.institutionName} - ${item.partnerCode}`, value: item.partnerCode }))
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    async getLinkedPfiByPartnerCode(partnerCode) {
      try {
        this.showSpinner = true
        const response = await this.getPfiByPartnerCode({
          partnerCode,
          permission: lmsPermissions.VIEW_COLLATERAL_INFORMATION,
        })

        const { data, status } = await response
        if (status === 200 && data) {
          this.collateralFormData = {
            ...this.collateralFormData,
            partnerCode: data.data[0].partnerCode,
            partnerName: data.data[0].institutionName,
            partnerEmailAddress: data.data[0].institutionEmail,
          }
          this.showSpinner = false
        }
      } catch (error) {
        this.alertError({ text: error })
        this.showSpinner = false
      }
    },
    async handleSubmit(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.collateralRules.validate().then(async success => {
        if (success) {
          this.confirm(this.collateralManagementAction, { text: `Are you sure you want to ${this.buttonLabel} this Collateral Information?` })
        } else {
          const errorMessages = []
          Object.keys(this.$refs.collateralRules.errors).forEach(key => {
            const value = this.$refs.collateralRules.errors[key]

            if (Array.isArray(value) && value.length > 0) {
              value.forEach(error => {
                errorMessages.push(`${error}`)
              })
            }
          })
          this.allValidationErrors = errorMessages
          this.$root.$emit('bv::show::modal', 'all-validated-modal')
        }
      })
    },
    async collateralManagementAction() {
      try {
        this.isLoading = true
        const formData = {
          ...this.collateralFormData,
          collateralValuation: this.collateralFormData.collateralValuation ? toKobo(this.collateralFormData.collateralValuation) : '',
          documentIdentifier: this.documentIdentifier,
        }
        const response = this.buttonLabel === 'Save'
          ? await this.createCollateral({ formData, permission: lmsPermissions.ADD_COLLATERAL_INFORMATION })
          : await this.updateCollateral({ formData, permission: lmsPermissions.MODIFY_COLLATERAL_INFORMATION })
        const { status } = await response
        if (status === 200) {
          this.fetchCollateralManagement()
          this.hideModal('modal-lg')
          this.alertSuccess({ text: `Your request to ${this.buttonLabel} Collateral Information was successful` })
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          this.alertError({ text: `Your request to ${this.buttonLabel} Collateral Information failed. Reason: ${responseError.errorMessage}` })
        })
      } finally {
        this.isLoading = false
      }
    },

    async onSelectCollateralItem(items, activity, val) {
      if (val === 'view') {
        this.isViewOnly = true
      }
      this.fetchCollateralDocumentById(items.id)
      this.isEditable = true
      this.collateralFormData.partnerCode = items.partnerCode
      this.collateralFormData.partnerName = items.partnerName
      this.collateralFormData.partnerEmailAddress = items.partnerEmailAddress
      this.collateralFormData.collateralType = items.collateralType
      this.collateralFormData.collateralValuation = items.collateralValuation ? toBalance(items.collateralValuation) : ''
      this.collateralFormData.collateralDescription = items.collateralDescription
      this.collateralFormData.location = items.location
      this.collateralFormData.currencyCode = items.currencyCode
      this.collateralFormData.perfectionStatus = items.perfectionStatus
      this.collateralFormData.enforceValidation = items.enforceValidation
      this.documentIdentifier = items.documentIdentifier
      this.collateralFormData.id = items.id
      this.isEditable = true
      this.isReadonly = val === 'edit' ? this.isReadonly = false : this.isReadonly = true
      this.modalTitle = val === 'edit' ? 'Edit Collateral Information' : 'View Collateral Information'
      this.buttonLabel = 'Update'
      this.$root.$emit('bv::show::modal', 'modal-lg')
    },

    async fetchCollateralDocumentById(id) {
      try {
        this.isBusy = true
        const response = await this.getCollateralDocumentById({
          id,
          permission: lmsPermissions.VIEW_COLLATERAL_INFORMATION,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.documentsFormData = data.data.documents
            ? data.data.documents.map(item => ({
              url: item.url,
              type: item.typeID,
              fileName: item.type,
              id: item.id,
              fileExtension: item.fileExtension,
            }))
            : [
              {
                url: '',
                type: '',
              },
            ]
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
      return true
    },
    async collateralInfoStatus(items) {
      const {
        id, partnerCode, partnerName, partnerEmailAddress, collateralDescription, referenceCode, collateralType, collateralValuation, location, perfectionStatus,
      } = items
      const actionType = items.status === 'Active' ? 'InActive' : 'Active'
      const action = items.status === 'Active' ? 'Deactivate' : 'Activate'
      const permission = items.status === 'Active' ? 'DEACTIVATE_COLLATERAL_INFORMATION' : 'ACTIVATE_COLLATERAL_INFORMATION'

      try {
        const updateStatus = async () => {
          this.showSpinner = true
          const statusValues = {
            id,
            partnerCode,
            partnerName,
            partnerEmailAddress,
            name: collateralDescription,
            collateralType,
            collateralValuation,
            location,
            perfectionStatus,
            referenceCode,
          }
          const response = await this.updateCollateralStatus({
            statusValues, actionType, routeName: 'CollateralInformation', permission,
          })
          if (response.status === 200) {
            this.fetchCollateralManagement()
            this.alertSuccess({ text: `Your request to ${action} ${statusValues.name} Collateral Information was successful` })
            this.showSpinner = false
          }
        }
        this.confirm(updateStatus, { title: '', text: `Are you sure you want to ${action} this Collateral Information?` })
      } catch (error) {
        this.alertError({ text: `Your request to ${action} Collateral Information failed. Reason: ${error}` })
      } finally {
        this.showSpinner = false
      }
    },
    resetModal() {
      this.collateralFormData = { ...null }
      this.isReadonly = false
      this.isLoading = false
      this.isEditable = false
      this.isViewOnly = false
      this.documentsFormData = [{
        type: '',
        url: '',
        file: '',
        loading: false,
      }]
      this.modalTitle = 'Create A Collateral Information'
      this.buttonLabel = 'Save'
    },
    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchCollateralManagement()
    },
  },
}
</script>

<style lang="scss" scoped>
.upload-image{
    border: 2px dashed #DCD9D9;
    position: relative;
    width: 250px;
    height: 250px
}
.format {
    color: #999999 !important;
}
form input[type=file]{
  position: absolute;
  margin: 0;
  padding: 0;
  width: 100%;
  height: 100%;
  outline: none;
  opacity: 0;
}
.upload-instruction{
  padding-top: 22%;
  color: #000;
  text-align: center;
}
.trash {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 1;
  background: #fff;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 10px;
  margin-right: 10px;
  cursor: pointer;
}
.view-details {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  background: #fff;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 10px;
  margin-left: 10px;
  cursor: pointer;
}
.btn-text-right {
 text-align: right;
}
</style>
